import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import BaseModal, { modalStyles, HoverButton } from './BaseModal';
import ModalStickyFooter from '../ui/ModalStickyFooter';
import { FontAwesome6 } from '@expo/vector-icons';
import { colors, typography } from '../../styles/theme';

interface AdvancedSettingsInfoModalProps {
  visible: boolean;
  onClose: () => void;
  onBack?: () => void;
}

export default function AdvancedSettingsInfoModal({ visible, onClose, onBack }: AdvancedSettingsInfoModalProps) {
  return (
    <BaseModal
      visible={visible}
      onClose={onClose}
      header={onBack ? (
        <View style={styles.headerRow}>
          <TouchableOpacity onPress={onBack} style={styles.backButton}>
            <FontAwesome6 name="arrow-left" size={20} color={colors.text} solid />
          </TouchableOpacity>
          <Text style={[modalStyles.headerText, { flex: 1, marginBottom: 0 }]}>Vectorization Settings</Text>
          <View style={{ width: 44 }} />
        </View>
      ) : (
        <Text style={modalStyles.headerText}>Vectorization Settings</Text>
      )}
      footer={(
        <ModalStickyFooter>
          <HoverButton
            style={modalStyles.primaryButton}
            hoverStyle={modalStyles.primaryButtonHover}
            textStyle={modalStyles.primaryButtonText}
            onPress={onClose}
          >
            Got It
          </HoverButton>
        </ModalStickyFooter>
      )}
    >
      <View style={styles.container}>
        <Text style={styles.introText}>
          Fine-tune the algorithm that transforms your custom images into Paint-By-Pictures puzzles.
        </Text>

        <View style={styles.listItem}>
          <Text style={styles.bullet}>•</Text>
          <Text style={styles.listContent}>
            <Text style={styles.paramName}>Min Dust Area: </Text>
            Cleans up noise. Increases the minimum size a colored speck must be to become a puzzle piece. Turn this up if your puzzle has too many tiny, unpaintable dots.
          </Text>
        </View>

        <View style={styles.listItem}>
          <Text style={styles.bullet}>•</Text>
          <Text style={styles.listContent}>
            <Text style={styles.paramName}>RDP Epsilon: </Text>
            Controls path simplification. Lower values create highly detailed, complex puzzle pieces that strictly follow the image. Higher values create smoother, simpler, blockier pieces.
          </Text>
        </View>

        <View style={styles.listItem}>
          <Text style={styles.bullet}>•</Text>
          <Text style={styles.listContent}>
            <Text style={styles.paramName}>Corner Angle: </Text>
            Determines how sharp a corner can be before it gets smoothed out.
          </Text>
        </View>

        <View style={styles.listItem}>
          <Text style={styles.bullet}>•</Text>
          <Text style={styles.listContent}>
            <Text style={styles.paramName}>Max Error: </Text>
            Controls curve accuracy. A higher max error allows the puzzle lines to be looser and smoother, while a lower error forces them to perfectly hug the original pixels.
          </Text>
        </View>
      </View>
    </BaseModal>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    width: '100%',
  },
  backButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    paddingBottom: 16,
    paddingHorizontal: 8,
  },
  introText: {
    ...typography.body,
    color: colors.textMuted,
    textAlign: 'left',
    marginBottom: 24,
    fontSize: 16,
    lineHeight: 22,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  bullet: {
    ...typography.bodyBold,
    color: colors.primary,
    fontSize: 20,
    marginRight: 10,
    lineHeight: 22,
  },
  listContent: {
    ...typography.body,
    color: colors.text,
    flex: 1,
    fontSize: 16,
    lineHeight: 22,
  },
  paramName: {
    ...typography.bodyBold,
    color: colors.text,
    fontWeight: 900,
  },
});
